import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Shield, CreditCard, Bell, CheckCircle, Wallet, Car, User, ArrowRight } from 'lucide-react';

const STEPS = {
  rider: [
    { Icon: MapPin,     title: 'Book a ride',       desc: 'Set pickup and drop. Nearby totos appear on the map instantly.' },
    { Icon: Shield,     title: 'Ride safely',       desc: 'Every driver is verified. Track your trip live, share with family.' },
    { Icon: CreditCard, title: "Pay what's fair",   desc: 'No platform fees, no hidden charges, no surprise surge.' },
  ],
  driver: [
    { Icon: Bell,        title: 'Instant alert',    desc: 'A new ride request shows route, distance, and earnings upfront.' },
    { Icon: CheckCircle, title: 'Accept or skip',   desc: 'No pressure, no penalty. You decide which rides to take.' },
    { Icon: Wallet,      title: 'Keep more, earn more', desc: 'Lowest commission in the business, paid out instantly.' },
  ],
};

const CTA = {
  rider: { Icon: User, label: 'Download Rider App', url: 'https://play.google.com/store/apps/details?id=com.elt.totowala' },
  driver: { Icon: Car, label: 'Download Driver App', url: 'https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers' },
};

const COLOR_CLASSES = {
  teal:    { text: 'text-teal-dark dark:text-teal', solid: 'bg-teal-dark' },
  primary: { text: 'text-primary',                   solid: 'bg-primary' },
};

const HowItWorks = () => {
  const [tab, setTab] = useState('rider');
  const accent = tab === 'rider' ? 'primary' : 'teal';
  const c = COLOR_CLASSES[accent];
  const cta = CTA[tab];

  return (
    <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
      <div className="max-w-[760px] mx-auto">

        <div className="text-center mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-ink dark:text-dark-ink mb-3">
            How it works
          </h2>
          <p className="text-base text-ink-soft dark:text-dark-ink-soft">
            Three steps. Two journeys.
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="inline-flex p-1 rounded-full bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line">
            {[
              { key: 'rider', label: 'For Riders' },
              { key: 'driver', label: 'For Drivers' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === key ? 'text-white' : 'text-ink-soft dark:text-dark-ink-soft'
                }`}
              >
                {tab === key && (
                  <motion.span
                    layoutId="how-it-works-tab-bg"
                    className={`absolute inset-0 rounded-full ${COLOR_CLASSES[key === 'rider' ? 'primary' : 'teal'].solid}`}
                    transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                  />
                )}
                <span className="relative">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="relative mb-8"
          >
            <div className="absolute left-5 top-5 bottom-5 w-px bg-line dark:bg-dark-line" aria-hidden="true" />
            <div className="space-y-6">
              {STEPS[tab].map((s, i) => (
                <div key={s.title} className="relative flex gap-4">
                  <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${c.solid} text-white text-sm font-bold`}>
                    {i + 1}
                  </div>
                  <div className="pt-1.5">
                    <div className="flex items-center gap-2 mb-1">
                      <s.Icon size={15} className={c.text} aria-hidden="true" />
                      <h3 className="text-base font-bold text-ink dark:text-dark-ink">{s.title}</h3>
                    </div>
                    <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-center">
          <a
            href={cta.url}
            target="_blank" rel="noopener noreferrer"
            className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-control font-semibold text-sm text-white transition-opacity hover:opacity-90 ${c.solid}`}
          >
            <cta.Icon size={16} />
            {cta.label}
            <ArrowRight size={16} strokeWidth={2.5} aria-hidden="true" />
          </a>
        </div>

      </div>
    </section>
  );
};

export default HowItWorks;
